/**
 * useMaterialStore.ts — persisted material catalog store (MAT-04).
 *
 * Pattern: mirrors useServiceStore.ts exactly (applyUpdater helper, persist config, CRUD setters).
 * Storage key: 'estimatix-materials' — registered in storageSchemaMap (Plan 01 §Security).
 * DoS mitigation: max 500 catalog entries (enforced by storageSchemaMap Zod schema, not here).
 *
 * FSD BOUNDARY: store layer reads/writes the catalog only. It does NOT call buildBOM or
 * computeMaterialTotals — those are called from the feature hook (useMaterials).
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { generateId } from '@/shared/lib/id';
import type { Material } from '@/entities/material';

// ---------------------------------------------------------------------------
// applyUpdater — copy verbatim from useServiceStore.ts
// ---------------------------------------------------------------------------

function applyUpdater<T>(updater: T | ((prev: T) => T), prev: T): T {
  return typeof updater === 'function' ? (updater as (prev: T) => T)(prev) : updater;
}

// ---------------------------------------------------------------------------
// State interface
// ---------------------------------------------------------------------------

interface MaterialState {
  materials: Material[];
  setMaterials: (updater: Material[] | ((prev: Material[]) => Material[])) => void;
  createMaterial: (data: Partial<Material>) => Material;
  updateMaterial: (id: string, updates: Partial<Material>) => void;
  deleteMaterial: (id: string) => boolean;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useMaterialStore = create<MaterialState>()(
  persist(
    (set) => ({
      materials: [],

      setMaterials: (updater) => set((state) => ({
        materials: applyUpdater(updater, state.materials),
      })),

      createMaterial: (data) => {
        const newMaterial: Material = {
          id: data.id || generateId(),
          name: data.name || '',
          nameKey: data.nameKey,
          unit: data.unit || 'szt',
          pricePerUnit: Math.max(0, Number(data.pricePerUnit) || 0),
          categoryId: data.categoryId,
        };
        set((state) => ({ materials: [newMaterial, ...state.materials] }));
        return newMaterial;
      },

      updateMaterial: (id, updates) => set((state) => ({
        materials: state.materials.map((m) => {
          if (m.id !== id) return m;
          const finalUpdates = { ...updates };
          // If user sets a new name, clear the i18n key (same discipline as useServiceStore)
          if (updates.name && m.nameKey) {
            finalUpdates.nameKey = undefined;
          }
          if (updates.pricePerUnit !== undefined) {
            finalUpdates.pricePerUnit = Math.max(0, Number(updates.pricePerUnit) || 0);
          }
          return { ...m, ...finalUpdates };
        }),
      })),

      deleteMaterial: (id) => {
        set((state) => ({
          materials: state.materials.filter((m) => m.id !== id),
        }));
        return true;
      },
    }),
    {
      name: 'estimatix-materials',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
